import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, ArrowRight, Search, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { SortableTh, sortRows, type TableSort, type SortKind } from '@/components/ui/sortable-table';
import { fetchMduQueue } from '@/hooks/useMduRequests';
import { useAuth } from '@/hooks/useAuth';
import { isGfArea } from '@uniticket/shared';
import type { MduRequestQueueItem, MduCase } from '@uniticket/shared';

// How many requests are pulled per call. The server computes one live aggregate over public.address for each
// LISTED row, so the list is fetched in slices rather than in one shot — "load more" appends the next slice.
const PAGE_SIZE = 100;

// Case → short label + pill colour. The labels come from i18n (mdu.caseN.short).
const CASE_STYLES: Record<MduCase, string> = {
  1: 'bg-sky-50 text-sky-700',
  2: 'bg-amber-50 text-amber-700',
  3: 'bg-fuchsia-50 text-fuchsia-700',
  4: 'bg-emerald-50 text-emerald-700',
};

// ── Sortable columns ────────────────────────────────────────────────────────────────────────────
// « On doit pouvoir trier sur le type de case » (user, 2026-08-12) : le cas décide de la manœuvre, donc
// grouper la file par cas, c'est décider par quel lot on attaque. Les autres colonnes suivent, avec le
// composant partagé — une seule implémentation du tri dans l'app.
//
// Le cas se déclare 'text' à dessein : `kind` ne choisit QUE la direction du PREMIER clic, et un cas est
// un rang, pas une grandeur — on veut Cas 1 → Cas 4, pas l'inverse (« rien à faire » d'abord). sortRows
// compare bien les valeurs comme des NOMBRES. Le tri de JS étant stable, chaque bloc de cas reste trié
// dans l'ordre du serveur : commune, rue, numéro — celui du document de référence.
const COLUMNS: { key: string; labelKey: string; kind: SortKind }[] = [
  { key: 'id', labelKey: 'mdu.queue.colId', kind: 'number' },
  { key: 'place', labelKey: 'mdu.queue.colPlace', kind: 'text' },
  { key: 'postal', labelKey: 'mdu.queue.colPostal', kind: 'text' },
  { key: 'address', labelKey: 'mdu.queue.colAddress', kind: 'text' },
  { key: 'contractor', labelKey: 'mdu.queue.colContractor', kind: 'text' },
  { key: 'case', labelKey: 'mdu.queue.colCase', kind: 'text' },
  { key: 'counts', labelKey: 'mdu.queue.colCounts', kind: 'number' },
];

const COLUMN_VALUE: Record<string, (it: MduRequestQueueItem) => string | number | null> = {
  id: (it) => it.id,
  place: (it) => it.municipality ?? it.city,
  // Le CP vient des adresses APPARIÉES, pas de la demande (elle n'en porte aucun) : trier dessus
  // regroupe donc par localité réelle, ce que la colonne « commune » ne sait pas faire.
  postal: (it) => it.postalCodes[0] ?? null,
  address: (it) => [it.streetName, it.hnrFull].filter(Boolean).join(' ') || null,
  contractor: (it) => it.contractor,
  case: (it) => it.mduCase,
  counts: (it) => it.nbLogements,
};

// Le tri survit à l'aller-retour vers une demande. Sans ça il mourrait au retour de la fiche, et c'est
// exactement l'irritant qu'Anouk a signalé sur l'autre file le 10-08 (« il ne sauvegarde pas mon filtre »).
const SORT_STORAGE_KEY = 'mdu-queue-sort';
// Le filtre « à moi ou à personne » survit lui aussi à l'aller-retour vers une demande, même patron.
const MINE_STORAGE_KEY = 'mdu-queue-mine';

function readSort(): TableSort | null {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    // Une clé inconnue (colonne renommée ou retirée) est ABANDONNÉE, jamais appliquée à l'aveugle.
    return typeof p?.key === 'string' && COLUMN_VALUE[p.key] && (p.dir === 'asc' || p.dir === 'desc')
      ? { key: p.key, dir: p.dir }
      : null;
  } catch {
    return null;
  }
}

function saveSort(sort: TableSort | null) {
  try {
    if (sort) localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
    else localStorage.removeItem(SORT_STORAGE_KEY);
  } catch {
    /* stockage indisponible : le tri reste valable pour la session */
  }
}

function CaseBadge({ mduCase }: { mduCase: MduCase }) {
  const { t } = useTranslation('tickets');
  return (
    <span className={cn('text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap', CASE_STYLES[mduCase])}>
      {t(`mdu.case${mduCase}.short`)}
    </span>
  );
}

/** « Le dossier est chez X » — même forme et mêmes couleurs que la file de validation, à dessein :
 *  les deux files se lisent de la même façon. */
function OwnerBadge({ item, meId }: { item: MduRequestQueueItem; meId?: string }) {
  const { t } = useTranslation('tickets');
  if (!item.ownerId) return null;
  const mine = item.ownerId === meId;
  return (
    <span className={cn('ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap',
      mine ? 'bg-violet-600 text-white' : 'bg-violet-100 text-violet-700')}
      title={mine ? t('mdu.queue.ownedByMeTitle') : t('mdu.queue.ownedByTitle', { name: item.ownerName ?? '' })}>
      {mine ? t('mdu.queue.ownedByMe') : t('mdu.queue.ownedBy', { name: item.ownerName ?? '—' })}
    </span>
  );
}

/**
 * La zone de déploiement, et on ne marque QUE l'exception.
 *
 * GF → pastille : « si ça commence en GF je dois avoir un moyen de le voir rapidement, car on ne traite
 * pas ce genre de ticket » (user, 2026-09-03) — le but est de ne PAS ouvrir le dossier, donc l'endroit
 * utile est la file. UF → rien : le cas normal ne mérite pas de bruit, et 146 des 260 zones sont UF.
 *
 * ⛔ ET RIEN NON PLUS QUAND LA DEMANDE N'APPARIE AUCUNE ADRESSE (user, 2026-09-07, capture à l'appui :
 * huit lignes « À créer » d'affilée portant toutes « zone ? »). Une adresse qui n'existe pas encore dans
 * le DMP n'a évidemment pas de zone — la pastille du cas le dit déjà, et la répéter à côté ne fait
 * qu'occuper la colonne sans rien apprendre. Le seul état qui mérite d'être signalé est celui qui
 * SURPREND : des logements appariés, et pas un seul qui porte une zone.
 *
 * `isGfArea` vient de @uniticket/shared : la règle du préfixe n'est pas réécrite ici.
 */
function AreaBadge({ item }: { item: MduRequestQueueItem }) {
  const { t } = useTranslation('tickets');
  const gf = item.areaCodes.filter(isGfArea);
  if (gf.length > 0) {
    return (
      <span className="ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 whitespace-nowrap"
            title={t('mdu.queue.gfBadgeTitle', { codes: gf.join(' · ') })}>
        {t('mdu.queue.gfBadge')}
      </span>
    );
  }
  // Une zone connue, ou aucune adresse à qui en demander une : dans les deux cas, rien à dire.
  if (item.areaCodes.length > 0 || item.nbLogements === 0) return null;
  return (
    <span className="ml-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap"
          title={t('mdu.queue.areaUnknownTitle')}>
      {t('mdu.queue.areaUnknown')}
    </span>
  );
}


export function MduRequestQueue() {
  const { t } = useTranslation('tickets');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<MduRequestQueueItem[]>([]);
  const [total, setTotal] = useState(0);
  // Quand l'instantané du serveur a été pris. Affiché : une donnée en cache doit dire de quand elle
  // date, sinon on ne sait plus si l'on regarde l'état de la base ou celui d'il y a dix minutes.
  const [takenAt, setTakenAt] = useState<string | null>(null);
  // This queue shows PENDING requests and nothing else (user, 2026-08-12: "j'ai uniquement besoin des
  // pending") — the exact set the reference doc's query returns: NOT EXISTS in scope.mdu_request_temp,
  // ordered commune / street / house number. The 1853 answered requests are archive, not work: listing them
  // buried the 47 that need action under a first page that was 100% already handled. The server still takes
  // a `status` argument, so restoring the history is a one-line change if it is ever asked for.
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Filtres d'escalade. Ils partent au SERVEUR : filtrer le tableau reçu ferait mentir « N sur T ».
  const [mine, setMine] = useState(() => {
    try { return localStorage.getItem(MINE_STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [escalatedToMe, setEscalatedToMe] = useState(false);
  const [isEscalationTarget, setIsEscalationTarget] = useState(false);
  // `null` = l'ordre du serveur (commune / rue / numéro), celui du document de référence.
  const [sort, setSort] = useState<TableSort | null>(() => readSort());
  // Free-text search (id / street / municipality / city / house number / contractor), debounced.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(h);
  }, [searchInput]);

  // Token of the newest fetch: an answer that lands after the filter or the search moved on is dropped
  // instead of overwriting the list (or appending a slice of the previous filter to it).
  const reqRef = useRef(0);

  const load = useCallback(async (refresh = false) => {
    const token = ++reqRef.current;
    setLoading(true);
    try {
      const res = await fetchMduQueue('pending', search, {
        limit: PAGE_SIZE, offset: 0, refresh,
        mine: escalatedToMe ? undefined : mine,
        view: escalatedToMe ? 'escalatedToMe' : undefined,
      });
      if (token !== reqRef.current) return;
      setItems(res.data);
      setTotal(res.meta.total);
      setTakenAt(res.meta.takenAt ?? null);
      setIsEscalationTarget(!!res.meta.isEscalationTarget);
    } catch (err: any) {
      if (token !== reqRef.current) return;
      toast.error(err.message || t('mdu.queue.loadError'));
    } finally {
      if (token === reqRef.current) setLoading(false);
    }
  }, [search, mine, escalatedToMe, t]);

  useEffect(() => { load(); }, [load]);

  const applySort = useCallback((next: TableSort) => { setSort(next); saveSort(next); }, []);
  // Le tri porte sur ce qui est CHARGÉ. Les 47 demandes en cours tiennent dans une tranche, donc il porte
  // sur tout ; si la file dépassait un jour la tranche, l'en-tête dit déjà « N affichées sur T ».
  const rows = useMemo(
    () => sortRows(items, sort, (it, key) => COLUMN_VALUE[key]?.(it) ?? null),
    [items, sort],
  );

  const loadMore = useCallback(async () => {
    const token = reqRef.current;
    setLoadingMore(true);
    try {
      const res = await fetchMduQueue('pending', search, {
        limit: PAGE_SIZE, offset: items.length,
        mine: escalatedToMe ? undefined : mine,
        view: escalatedToMe ? 'escalatedToMe' : undefined,
      });
      if (token !== reqRef.current) return;
      setItems((prev) => [...prev, ...res.data]);
      setTotal(res.meta.total);
    } catch (err: any) {
      if (token !== reqRef.current) return;
      toast.error(err.message || t('mdu.queue.loadError'));
    } finally {
      if (token === reqRef.current) setLoadingMore(false);
    }
  }, [search, items.length, mine, escalatedToMe, t]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('mdu.queue.title')}</h2>
          {/* The count is the REAL size of the filtered set, so a partially loaded list reads "100 of 1885"
              instead of passing its first slice off as the whole thing. */}
          <p className="text-sm text-slate-500">
            {items.length < total
              ? t('mdu.queue.subtitleShown', { shown: items.length, total })
              : t('mdu.queue.subtitle', { count: total })}
          </p>
        </div>
      </div>

      {/* Search + l'état de l'instantané. Le bouton est à côté de la recherche parce que c'est là qu'on
          regarde quand on doute de ce qu'on voit. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('mdu.queue.searchPlaceholder')}
            className="pl-9"
            aria-label={t('mdu.queue.searchPlaceholder')}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => void load(true)} disabled={loading}>
          <RefreshCw className={cn('mr-1.5 h-4 w-4', loading && 'animate-spin')} />
          {t('mdu.queue.refresh')}
        </Button>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none"
               title={t('mdu.queue.mineHint')}>
          <input type="checkbox" checked={mine} disabled={escalatedToMe}
            onChange={(e) => {
              setMine(e.target.checked);
              try { localStorage.setItem(MINE_STORAGE_KEY, e.target.checked ? '1' : '0'); } catch { /* stockage indisponible */ }
            }}
            className="rounded border-slate-300 text-brand-rose focus:ring-brand-rose/40 disabled:opacity-30" />
          {t('mdu.queue.mineFilter')}
        </label>
        {/* Offerte aux seuls membres de la chaîne : ailleurs, ce filtre ne rendrait jamais rien. */}
        {isEscalationTarget && (
          <button type="button" onClick={() => setEscalatedToMe((v) => !v)}
            className={cn('rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
              escalatedToMe
                ? 'border-violet-300 bg-violet-100 text-violet-700'
                : 'border-slate-200 text-slate-500 hover:bg-slate-50')}>
            {t('mdu.queue.filterEscalatedToMe')}
          </button>
        )}
        {takenAt && (
          <span className="text-[11px] text-slate-400">
            {t('mdu.queue.takenAt', {
              time: new Date(takenAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
            })}
          </span>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-400">
          {t('mdu.queue.empty')}
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                  {COLUMNS.map((c) => (
                    <SortableTh
                      key={c.key}
                      label={t(c.labelKey)}
                      sortKey={c.key}
                      kind={c.kind}
                      sort={sort}
                      onSort={applySort}
                      title={t('mdu.queue.sortHint')}
                      className="px-4"
                    />
                  ))}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => {
                  const go = () => navigate(`/mdu-requests/${it.id}`);
                  return (
                    <tr
                      key={it.id}
                      role="link"
                      tabIndex={0}
                      aria-label={t('mdu.queue.reviewAria', { id: it.id })}
                      onClick={go}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
                      className="border-t border-slate-100 transition-colors cursor-pointer hover:bg-brand-rose/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose/40 focus-visible:ring-inset"
                    >
                      <td className="px-4 py-3 font-medium text-slate-800">{it.id}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {it.municipality ?? '—'}
                        {it.city && it.city !== it.municipality && (
                          <span className="text-slate-400"> · {it.city}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {it.postalCodes.length === 0 ? <span className="text-slate-300">—</span> : (
                          <span className={cn(it.postalCodes.length > 1
                            && 'rounded px-1.5 py-0.5 ring-1 ring-amber-300 bg-amber-50 text-amber-800 font-medium')}
                            title={it.postalCodes.length > 1
                              ? t('mdu.queue.multiPostalTitle', { count: it.postalCodes.length })
                              : undefined}>
                            {it.postalCodes.join(' · ')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {[it.streetName, it.hnrFull].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{it.contractor ?? '—'}</td>
                      <td className="px-4 py-3">
                        <CaseBadge mduCase={it.mduCase} />
                        {/* Le cas a été calculé sur un appariement ÉLARGI : le dire dans la file, pas
                            seulement dans la fiche — c'est ici qu'on décide par quoi commencer. */}
                        {it.suffixMismatch && (
                          <span className="ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap"
                                title={t('mdu.queue.suffixMismatchTitle')}>
                            {t('mdu.queue.suffixMismatch')}
                          </span>
                        )}
                        <AreaBadge item={it} />
                        <OwnerBadge item={it} meId={user?.id} />
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {t('mdu.queue.countsValue', { total: it.nbLogements, scope: it.nbLogementsScopeV15 })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ArrowRight className="inline-block w-4 h-4 text-brand-rose/70" aria-hidden="true" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {items.length < total && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="text-sm font-semibold px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-brand-rose/40 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? t('mdu.queue.loadingMore') : t('mdu.queue.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
